/**
 * AuditAnswerPage.jsx — Answer review with centered chain modal
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useHistory, useLocation as useRouterLocation } from "react-router-dom";
import Sidebar from "../sidebar/sidebar";
import Header from "../header/header";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config.json";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { injectAuditStyles } from "./auditStyles";
import { StatusBadge } from "./AuditIcons";

const SESSION_KEY = "audit_answer_page_v6";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};

/* ── Avatar ── */
const AV_COLORS = ["#2563eb", "#0d9488", "#dc2626", "#d97706", "#7c3aed", "#0891b2"];
const Avatar = ({ name = "", id, size = 24 }) => {
  const p = (name || "").split(" ");
  const ini = [(p[0]?.[0] || ""), (p[1]?.[0] || "")].join("").toUpperCase() || "?";
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: AV_COLORS[Number(id || 0) % AV_COLORS.length], color: "#fff", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * .34, fontWeight: 700 }}>
      {ini}
    </div>
  );
};

/* ── Stat ── */
const Stat = ({ label, value, color, bg, border }) => (
  <div style={{ padding: "7px 14px", borderRadius: 7, background: bg, border: `1px solid ${border}`, textAlign: "center", minWidth: 68 }}>
    <div style={{ fontSize: 9, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: ".09em", marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 19, fontWeight: 800, color, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{value}</div>
  </div>
);

/* ══════════════════════════════════════════
   CHAIN MODAL — centered full-screen overlay
══════════════════════════════════════════ */
const CHAIN_SC = {
  APPROVED:  { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Approved",  icon: "✓" },
  ACCEPTED:  { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Approved",  icon: "✓" },
  REJECTED:  { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Rejected",  icon: "✕" },
  PENDING:   { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: "Pending",   icon: "●" },
  WAITING:   { color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb", label: "Waiting",   icon: "○" },
  IN_REVIEW: { color: "#2563eb", bg: "#eff6ff", border: "#dbeafe", label: "In Review", icon: "◎" },
};

const ChainModal = ({ auditLevels = [], onClose }) => {
  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const completed = auditLevels.filter(l => ["APPROVED", "ACCEPTED"].includes((l.status || "").toUpperCase())).length;

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(4px)" }}
    >
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,.18)", overflow: "hidden", animation: "auditScaleIn .18s ease" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #f3f4f6" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Audit Chain</div>
            <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 2 }}>
              {completed} of {auditLevels.length} level{auditLevels.length !== 1 ? "s" : ""} completed
            </div>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e5e7eb", borderRadius: 7, background: "transparent", cursor: "pointer", fontSize: 18, color: "#6b7280", lineHeight: 1 }}>×</button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: "#f3f4f6" }}>
          <div style={{ height: "100%", width: `${auditLevels.length ? (completed / auditLevels.length) * 100 : 0}%`, background: completed === auditLevels.length ? "#16a34a" : "#2563eb", transition: "width .4s ease" }} />
        </div>

        {/* Chain steps */}
        <div style={{ padding: "4px 0 6px" }}>
          {auditLevels.map((lvl, i) => {
            const sc = CHAIN_SC[(lvl.status || "WAITING").toUpperCase()] || CHAIN_SC.WAITING;
            const isExt = (lvl.auditorType || "").toUpperCase() === "EXTERNAL";
            const isLast = i === auditLevels.length - 1;
            return (
              <div key={i}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "12px 20px" }}>
                  {/* Level indicator + connector */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: "50%",
                      background: sc.bg, border: `2px solid ${sc.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 800, color: sc.color,
                    }}>
                      {["APPROVED", "ACCEPTED"].includes((lvl.status || "").toUpperCase()) ? "✓" : `L${lvl.auditLevel}`}
                    </div>
                    {!isLast && <div style={{ width: 2, height: 24, background: "#f3f4f6", marginTop: 3 }} />}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                        Level {lvl.auditLevel}
                      </span>
                      <span style={{
                        fontSize: 9.5, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                        background: isExt ? "#f5f3ff" : "#eff6ff", color: isExt ? "#7c3aed" : "#2563eb",
                        border: `1px solid ${isExt ? "#ddd6fe" : "#dbeafe"}`,
                      }}>
                        {isExt ? "EXTERNAL" : "INTERNAL"}
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: "#374151", fontWeight: 500 }}>
                      {lvl.auditorName || `Auditor #${lvl.auditorId || "—"}`}
                    </div>
                    {lvl.auditedDate && (
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                        Actioned: {fmtDate(lvl.auditedDate)}
                      </div>
                    )}
                    {lvl.remark && (
                      <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 5, padding: "6px 10px", background: "#f9fafb", borderRadius: 6, border: "1px solid #f3f4f6", fontStyle: "italic" }}>
                        "{lvl.remark}"
                      </div>
                    )}
                  </div>

                  {/* Status badge */}
                  <div style={{ flexShrink: 0, paddingTop: 6 }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "4px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
                      background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`,
                    }}>
                      <span style={{ fontWeight: 800 }}>{sc.icon}</span> {sc.label}
                    </span>
                  </div>
                </div>
                {!isLast && <div style={{ height: 1, background: "#f9fafb", margin: "0 20px" }} />}
              </div>
            );
          })}
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "flex-end" }}>
          <button className="audit-btn-secondary" onClick={onClose} style={{ fontSize: 13 }}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ── Chain trigger button ── */
const ChainButton = ({ auditLevels = [] }) => {
  const [open, setOpen] = useState(false);
  if (!auditLevels.length) return null;
  const completed = auditLevels.filter(l => ["APPROVED", "ACCEPTED"].includes((l.status || "").toUpperCase())).length;
  const total = auditLevels.length;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          padding: "3px 10px", borderRadius: 20, fontSize: 10.5, fontWeight: 600, cursor: "pointer",
          border: "1px solid #e5e7eb", background: "#f9fafb", color: "#6b7280",
          display: "inline-flex", alignItems: "center", gap: 5, transition: "all .15s",
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: completed === total ? "#16a34a" : "#d97706", flexShrink: 0 }} />
        Chain {completed}/{total}
      </button>
      {open && <ChainModal auditLevels={auditLevels} onClose={() => setOpen(false)} />}
    </>
  );
};

/* ── Logic helpers ── */
const resolveMyEntry = (auditLevels = [], currentUserId) =>
  // eslint-disable-next-line eqeqeq
  auditLevels.find(l => l.auditorId == currentUserId) || null;

const prevLevelsAllApproved = (auditLevels = [], myEntry) => {
  if (!myEntry) return false;
  const myType = (myEntry.auditorType || "").toUpperCase();
  const toCheck = myType === "EXTERNAL"
    ? auditLevels.filter(l => (l.auditorType || "").toUpperCase() === "INTERNAL" || ((l.auditorType || "").toUpperCase() === "EXTERNAL" && l.auditLevel < myEntry.auditLevel))
    : auditLevels.filter(l => (l.auditorType || "").toUpperCase() === "INTERNAL" && l.auditLevel < myEntry.auditLevel);
  return toCheck.every(l => ["APPROVED", "ACCEPTED"].includes((l.status || "").toUpperCase()));
};

/* ── Confirmation Modal ── */
const ConfirmModal = ({ title, message, confirmLabel = "Confirm", confirmCls = "audit-btn-approve", onConfirm, onCancel, saving }) => (
  <div
    onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(3px)" }}
  >
    <div style={{ background: "#fff", borderRadius: 10, width: "100%", maxWidth: 380, boxShadow: "0 16px 40px rgba(0,0,0,.14)", overflow: "hidden" }}>
      <div style={{ padding: "16px 18px 14px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6 }}>{message}</div>
      </div>
      <div style={{ padding: "12px 18px 16px", display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #f3f4f6" }}>
        <button className="audit-btn-secondary" onClick={onCancel} disabled={saving} style={{ fontSize: 13 }}>Cancel</button>
        <button
          className={confirmCls}
          onClick={onConfirm}
          disabled={saving}
          style={{ fontSize: 13, minWidth: 110, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {saving
            ? <><span className="audit-spinner" style={{ width: 11, height: 11, borderWidth: 1.5, borderTopColor: "currentColor", borderColor: "rgba(0,0,0,.12)" }} /> Saving…</>
            : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

/* ── Inline reject panel ── */
const InlineReject = ({ onConfirm, onCancel, saving }) => {
  const [note, setNote] = useState("");
  return (
    <tr>
      <td colSpan={9} style={{ padding: 0 }}>
        <div style={{ padding: "12px 18px 14px 56px", background: "#fef2f2", borderTop: "1px dashed #fecaca", borderBottom: "1px solid #fecaca" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 7 }}>Rejection reason *</div>
          <textarea
            autoFocus rows={2} value={note} onChange={e => setNote(e.target.value)}
            placeholder="Explain why this answer is being rejected…"
            style={{ width: "100%", maxWidth: 500, resize: "none", fontSize: 12.5, padding: "8px 11px", borderRadius: 7, border: "1px solid #fca5a5", outline: "none", fontFamily: "inherit", color: "#111827", background: "#fff" }}
          />
          <div style={{ display: "flex", gap: 7, marginTop: 9 }}>
            <button onClick={() => note.trim() && onConfirm(note)} disabled={!note.trim() || saving} className="audit-btn-reject" style={{ fontSize: 12, padding: "5px 14px" }}>
              {saving ? "Saving…" : "Confirm"}
            </button>
            <button onClick={onCancel} className="audit-btn-secondary" style={{ fontSize: 12, padding: "5px 12px" }}>Cancel</button>
          </div>
        </div>
      </td>
    </tr>
  );
};

/* ── Shimmer row ── */
const ShimmerRow = ({ i }) => (
  <tr style={{ borderBottom: "1px solid #f3f4f6", opacity: 1 - i * .15 }}>
    <td colSpan={9} style={{ padding: "13px 16px" }}>
      <div style={{ background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "400% 100%", animation: "auditShimmer 1.3s ease infinite", height: 13, borderRadius: 4, width: `${40 + (i * 17) % 45}%` }} />
    </td>
  </tr>
);

/* ══════════════════════════════════════════
   ANSWER ROW
══════════════════════════════════════════ */
const AuditRow = ({ row, idx, questionId, fyId, kpiTitle, questionType, onViewDoc, onDone, selected, onToggle, isAdmin, locationMap }) => {
  const { currentUserId } = useCurrentUser();
  const [showReject, setShowReject] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingKind, setSavingKind] = useState("");
  const [confirm, setConfirm] = useState(false);

  const rawStatus   = (row.auditStatus || "PENDING").toUpperCase();
  const auditLevels = row.auditLevels || [];
  const myEntry     = resolveMyEntry(auditLevels, currentUserId);
  const myStatus    = myEntry ? (myEntry.status || "PENDING").toUpperCase() : null;
  const displayStatus = isAdmin ? rawStatus : (myStatus || rawStatus);
  const prevOK      = prevLevelsAllApproved(auditLevels, myEntry);
  const iMyPending  = myEntry && ["PENDING", "IN_REVIEW"].includes(myStatus);
  // eslint-disable-next-line eqeqeq
  const isCurrent   = Number(row.currentAuditorId) == Number(currentUserId);
  const canAct      = iMyPending && prevOK && isCurrent;
  const alreadyActed = myEntry && ["APPROVED", "ACCEPTED", "REJECTED"].includes(myStatus) && !["PENDING", "IN_REVIEW"].includes(myStatus);
  const waitingPrev = iMyPending && !prevOK;

  /* row.id is the audit step id */
  const stepId = row.id;

  /* Location */
  const locCode = locationMap?.[row.locationId]?.unitCode || "";

  /* Row background */
  const rowBg = selected ? "#eff6ff"
    : ["APPROVED", "ACCEPTED"].includes(displayStatus) ? (idx % 2 === 0 ? "#f0fdf4" : "#eafaf3")
    : displayStatus === "REJECTED" ? (idx % 2 === 0 ? "#fef2f2" : "#fce8e8")
    : "#fff";

  const accentClr = ["APPROVED", "ACCEPTED"].includes(displayStatus) ? "#16a34a"
    : displayStatus === "REJECTED" ? "#dc2626"
    : displayStatus === "IN_REVIEW" ? "#2563eb"
    : "#d97706";

  const submit = async (action, remark = "") => {
    setSaving(true); setSavingKind(action);
    try {
      await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}auditAction`, {},
        { questionId: Number(questionId), answerId: row.answerId, stepIds: [stepId], financialYearId: Number(fyId), action, remark, questionType, questionTitle: kpiTitle },
        "POST"
      );
      setShowReject(false); setConfirm(false); onDone?.();
    } finally { setSaving(false); setSavingKind(""); }
  };

  return (
    <>
      <tr style={{ background: rowBg, borderBottom: "1px solid #f3f4f6", transition: "background .1s", position: "relative" }}>
        {/* Accent + checkbox */}
        <td style={{ width: 46, padding: "0 6px 0 0", textAlign: "center", verticalAlign: "middle", position: "relative" }}>
          <div style={{ position: "absolute", left: 0, top: 5, bottom: 5, width: 3, borderRadius: "0 2px 2px 0", background: accentClr }} />
          {canAct
            ? <input type="checkbox" className="audit-checkbox" checked={selected} onChange={() => onToggle(stepId)} style={{ marginLeft: 10 }} />
            : <span style={{ fontSize: 10.5, color: "#d1d5db", fontWeight: 600 }}>{idx + 1}</span>
          }
        </td>

        {/* Period */}
        <td style={{ padding: "11px 12px", verticalAlign: "middle" }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>
            {fmtDate(row.fromDate)}
            {row.toDate && row.toDate !== row.fromDate && (
              <span style={{ color: "#9ca3af", fontWeight: 400 }}> – {fmtDate(row.toDate)}</span>
            )}
          </div>
          {row.textValue && (
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.textValue}</div>
          )}
        </td>

        {/* Value + Unit */}
        <td style={{ padding: "11px 12px", verticalAlign: "middle", width: 110 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: "#111827", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{row.value ?? "—"}</span>
          {row.unit && row.unit !== "-" && <span style={{ fontSize: 10.5, color: "#9ca3af", marginLeft: 4 }}>{row.unit}</span>}
        </td>

        {/* Location */}
        <td style={{ padding: "11px 12px", verticalAlign: "middle", width: 82 }}>
          {locCode
            ? <span style={{ fontSize: 11.5, fontWeight: 600, color: "#374151", background: "#f3f4f6", padding: "2px 8px", borderRadius: 5 }}>{locCode}</span>
            : <span style={{ color: "#e5e7eb", fontSize: 12 }}>—</span>
          }
        </td>

        {/* Submitted by */}
        <td style={{ padding: "11px 12px", verticalAlign: "middle", width: 155 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar name={row.submittedByName || `User #${row.submittedBy}`} id={row.submittedBy} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 96 }}>
                {row.submittedByName || `User #${row.submittedBy}`}
              </div>
              <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 1 }}>{fmtDate(row.submittedDate)}</div>
            </div>
          </div>
        </td>

        {/* Doc */}
        <td style={{ padding: "11px 12px", verticalAlign: "middle", width: 62, textAlign: "center" }}>
          {row.document?.documentMetadata?.url ? (
            <button
              onClick={() => { const m = row.document.documentMetadata; onViewDoc({ url: m.url, name: m.fileName || "File", mimeType: m.mimeType || "", isImage: /image\//i.test(m.mimeType || "") }); }}
              style={{ padding: "3px 10px", borderRadius: 5, border: "1px solid #dbeafe", background: "#eff6ff", color: "#2563eb", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
            >View</button>
          ) : <span style={{ fontSize: 12, color: "#e5e7eb" }}>—</span>}
        </td>

        {/* Status */}
        <td style={{ padding: "11px 12px", verticalAlign: "middle", width: 108, textAlign: "center" }}>
          <StatusBadge status={displayStatus} />
        </td>

        {/* Actions */}
        <td style={{ padding: "11px 14px", verticalAlign: "middle", width: 230 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 7, alignItems: "flex-start" }}>

            {/* Can act */}
            {canAct && !showReject && (
              <div style={{ display: "flex", gap: 6 }}>
                <button className="audit-btn-approve" onClick={() => setConfirm(true)} disabled={saving} style={{ fontSize: 12, padding: "5px 12px" }}>
                  {saving && savingKind === "ACCEPTED"
                    ? <span className="audit-spinner" style={{ width: 10, height: 10, borderWidth: 1.5, borderTopColor: "#16a34a", borderColor: "#bbf7d0" }} />
                    : "✓"
                  } Approve
                </button>
                <button className="audit-btn-reject" onClick={() => setShowReject(true)} disabled={saving} style={{ fontSize: 12, padding: "5px 12px" }}>
                  ✕ Reject
                </button>
              </div>
            )}

            {/* Already acted */}
            {alreadyActed && !showReject && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 11px",
                borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                background: myStatus === "REJECTED" ? "#fef2f2" : "#f0fdf4",
                color: myStatus === "REJECTED" ? "#dc2626" : "#16a34a",
                border: `1px solid ${myStatus === "REJECTED" ? "#fecaca" : "#bbf7d0"}`,
              }}>
                {myStatus === "REJECTED" ? "✕ Rejected by you" : "✓ Approved by you"}
              </span>
            )}

            {/* Waiting */}
            {waitingPrev && !showReject && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px", borderRadius: 20, fontSize: 11.5, fontWeight: 500, background: "#f9fafb", color: "#9ca3af", border: "1px solid #e5e7eb" }}>
                <span className="audit-spinner" style={{ width: 10, height: 10, borderWidth: 1.5, borderTopColor: "#9ca3af", borderColor: "#e5e7eb" }} />
                Awaiting prior level
              </span>
            )}

            {/* Not in queue */}
            {!myEntry && !canAct && (
              <span style={{ fontSize: 11.5, color: "#9ca3af" }}>
                {isAdmin && row.currentAuditorName
                  ? <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "#d97706" }} />{row.currentAuditorName}</span>
                  : "Not in your queue"}
              </span>
            )}

            {/* Chain button — always visible if levels exist */}
            {auditLevels.length > 0 && <ChainButton auditLevels={auditLevels} />}
          </div>
        </td>

        <td style={{ width: 4 }} />
      </tr>

      {showReject && (
        <InlineReject saving={saving && savingKind === "REJECTED"} onConfirm={note => submit("REJECTED", note)} onCancel={() => setShowReject(false)} />
      )}

      {confirm && (
        <ConfirmModal
          title="Confirm Approval"
          message={`Approve the answer for period ${fmtDate(row.fromDate)}, submitted by ${row.submittedByName || "this user"}?`}
          confirmLabel="✓ Approve"
          confirmCls="audit-btn-approve"
          saving={saving && savingKind === "ACCEPTED"}
          onConfirm={() => submit("ACCEPTED")}
          onCancel={() => setConfirm(false)}
        />
      )}
    </>
  );
};

/* ── Doc modal ── */
const DocModal = ({ info, onClose }) => {
  const { url, name, mimeType, isImage } = info;
  const isPdf = /pdf/i.test(mimeType || "") || /\.pdf(\?|$)/i.test(url || "");
  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(4px)" }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 860, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,.2)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 18px", borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{name}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <a href={url} target="_blank" rel="noreferrer" style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#f9fafb", color: "#374151", fontSize: 12, textDecoration: "none", fontWeight: 500 }}>Open in tab</a>
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #e5e7eb", background: "transparent", cursor: "pointer", fontSize: 18, color: "#6b7280", lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 18, background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240 }}>
          {isImage && <img src={url} alt={name} style={{ maxWidth: "100%", maxHeight: "68vh", borderRadius: 8 }} />}
          {isPdf && <iframe src={url} title={name} style={{ width: "100%", height: "68vh", border: "none", borderRadius: 8 }} />}
          {!isImage && !isPdf && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 36, opacity: .18, marginBottom: 12 }}>📄</div>
              <div style={{ fontWeight: 600, color: "#374151", marginBottom: 10 }}>Preview unavailable</div>
              <a href={url} target="_blank" rel="noreferrer" style={{ padding: "6px 16px", borderRadius: 7, background: "#2563eb", color: "#fff", fontSize: 12.5, fontWeight: 600, textDecoration: "none" }}>Download</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Bulk reject modal ── */
const BulkRejectModal = ({ count, onConfirm, onCancel, saving }) => {
  const [note, setNote] = useState("");
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onCancel(); }} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(4px)" }}>
      <div style={{ background: "#fff", borderRadius: 10, width: "100%", maxWidth: 420, boxShadow: "0 20px 50px rgba(0,0,0,.15)" }}>
        <div style={{ padding: "15px 18px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Reject {count} Answer{count !== 1 ? "s" : ""}</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Reason applies to all selected answers.</div>
        </div>
        <div style={{ padding: "16px 18px" }}>
          <label className="audit-label">Reason *</label>
          <textarea autoFocus rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Explain the rejection…" className="audit-input" style={{ resize: "none", fontSize: 13, lineHeight: 1.55 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={() => note.trim() && onConfirm(note)} disabled={!note.trim() || saving} className="audit-btn-reject" style={{ flex: 1, justifyContent: "center" }}>
              {saving ? "Saving…" : "Confirm Rejection"}
            </button>
            <button onClick={onCancel} className="audit-btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Filter bar ── */
const FilterBar = ({ fromDate, toDate, fyId, statusFilter, userFilter, locationFilter, fys, userOptions, locationList, onFromDate, onToDate, onFyId, onStatusFilter, onUserFilter, onLocationFilter, onClear, hasFilters, displayedCount, totalCount, loading }) => (
  <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap", paddingBottom: 13 }}>
    <div style={{ display: "flex", alignItems: "center", border: "1px solid #e5e7eb", borderRadius: 7, overflow: "hidden", background: "#fff", height: 34 }}>
      <span style={{ padding: "0 9px", fontSize: 9.5, color: "#9ca3af", fontWeight: 600, borderRight: "1px solid #f3f4f6", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap" }}>From</span>
      <input type="date" value={fromDate} onChange={e => onFromDate(e.target.value)} style={{ border: "none", outline: "none", padding: "0 9px", fontSize: 12.5, color: "#111827", background: "transparent", height: "100%", fontFamily: "inherit" }} />
    </div>
    <div style={{ display: "flex", alignItems: "center", border: "1px solid #e5e7eb", borderRadius: 7, overflow: "hidden", background: "#fff", height: 34 }}>
      <span style={{ padding: "0 9px", fontSize: 9.5, color: "#9ca3af", fontWeight: 600, borderRight: "1px solid #f3f4f6", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap" }}>To</span>
      <input type="date" value={toDate} min={fromDate} onChange={e => onToDate(e.target.value)} style={{ border: "none", outline: "none", padding: "0 9px", fontSize: 12.5, color: "#111827", background: "transparent", height: "100%", fontFamily: "inherit" }} />
    </div>

    {[
      { val: fyId, onChange: onFyId, opts: fys.map(f => ({ v: String(f.id), l: f.financial_year_value || f.name })), placeholder: "All Years", active: false },
      { val: statusFilter, onChange: onStatusFilter, opts: [{ v: "PENDING", l: "Pending" }, { v: "APPROVED", l: "Approved" }, { v: "REJECTED", l: "Rejected" }], placeholder: "All Statuses", active: !!statusFilter },
      { val: locationFilter, onChange: onLocationFilter, opts: (locationList || []).map(l => ({ v: String(l.id), l: l.unitCode })), placeholder: "All Locations", active: !!locationFilter },
      { val: userFilter, onChange: onUserFilter, opts: userOptions.map(u => ({ v: String(u.id), l: u.name })), placeholder: "All Users", active: !!userFilter, maxW: 150 },
    ].map(({ val, onChange, opts, placeholder, active, maxW }, i) => (
      <select
        key={i}
        style={{ height: 34, padding: "0 10px", borderRadius: 7, border: `1px solid ${active ? "#2563eb" : "#e5e7eb"}`, background: active ? "#eff6ff" : "#f9fafb", color: active ? "#2563eb" : "#374151", fontSize: 13, outline: "none", fontFamily: "inherit", fontWeight: active ? 600 : 400, ...(maxW ? { maxWidth: maxW } : {}) }}
        value={val} onChange={e => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    ))}

    {hasFilters && (
      <button onClick={onClear} style={{ fontSize: 12, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 20, padding: "4px 11px", cursor: "pointer", fontWeight: 600 }}>
        × clear
      </button>
    )}
    <div style={{ flex: 1 }} />
    {!loading && <span style={{ fontSize: 11.5, color: "#9ca3af" }}>{displayedCount} / {totalCount}</span>}
  </div>
);

/* ════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════ */
const AuditAnswerPage = (props) => {
  const router = useHistory();
  const routerLoc = useRouterLocation();
  const { currentUserId } = useCurrentUser();
  const isAdmin = Number(currentUserId) === 1;

  useEffect(() => { injectAuditStyles(); }, []);

  const navState = (() => {
    if (routerLoc.state?.questionId) {
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(routerLoc.state)); } catch { }
      return routerLoc.state;
    }
    try { const s = sessionStorage.getItem(SESSION_KEY); if (s) return JSON.parse(s); } catch { }
    const p = new URLSearchParams(routerLoc.search);
    return { questionId: p.get("questionId"), fyId: p.get("fyId"), locationIds: p.get("locationId"), title: p.get("title") || "KPI Review", questionType: p.get("questionType") || "", auditType: p.get("auditType") || "" };
  })();

  const { questionId, fyId: fyIdParam, locationIds: locationId, title: kpiTitle = "KPI Review", questionType = "", auditType = "" } = navState;

  const [sidebarExpanded, setSidebarExpanded]     = useState(true);
  const [rows, setRows]                           = useState([]);
  const [fys, setFys]                             = useState([]);
  const [locationList, setLocationList]           = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [error, setError]                         = useState(null);
  const [fyId, setFyId]                           = useState(fyIdParam || "");
  const [fromDate, setFromDate]                   = useState("");
  const [toDate, setToDate]                       = useState("");
  const [statusFilter, setStatusFilter]           = useState("PENDING");
  const [userFilter, setUserFilter]               = useState("");
  const [locationFilter, setLocationFilter]       = useState("");
  const [selected, setSelected]                   = useState(new Set());
  const [bulkRejectOpen, setBulkRejectOpen]       = useState(false);
  const [bulkSaving, setBulkSaving]               = useState(false);
  const [approvingAll, setApprovingAll]           = useState(false);
  const [confirmApproveAll, setConfirmApproveAll] = useState(false);
  const [bulkApproveConfirm, setBulkApproveConfirm] = useState(false);
  const [docModal, setDocModal]                   = useState(null);
  const [toast, setToast]                         = useState(null);

  const notify = (msg, type = "ok") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const locationMap = useMemo(() =>
    locationList.reduce((acc, l) => { acc[l.id] = l; return acc; }, {}),
  [locationList]);

  useEffect(() => {
    apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`, {}, {}, "GET")
      .then(res => { const list = res?.data?.data || []; setFys(list); if (!fyId && list.length) setFyId(String(list[list.length - 1].id)); })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, { userId: currentUserId }, "GET")
      .then(res => setLocationList(res?.data?.data || []))
      .catch(() => {});
  }, [currentUserId]);

  const load = useCallback(async (overrideFy) => {
    if (!questionId) { setError("No question ID."); setLoading(false); return; }
    const useFy = overrideFy ?? fyId;
    if (!useFy) return;
    setLoading(true); setError(null); setSelected(new Set());
    try {
      const qp = { questionId, financialYearId: useFy };
      if (locationId) qp.locationId = locationId;
      if (auditType) qp.auditorType = auditType;
      const res = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getAuditAnswers`, {}, qp, "GET");
      const data = res?.data?.data || res?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch { setError("Failed to load answers."); }
    finally { setLoading(false); }
  }, [questionId, fyId, locationId, auditType]);

  const prevFy = useRef("");
  useEffect(() => { if (fyId && fyId !== prevFy.current) { prevFy.current = fyId; load(fyId); } }, [fyId, load]);

  const userOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      const id = String(r.submittedBy ?? "");
      const name = r.submittedByName || `User #${r.submittedBy}`;
      if (id && !seen.has(id)) seen.set(id, { id, name });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const displayed = rows.filter(r => {
    if (statusFilter) {
      const myEntry = resolveMyEntry(r.auditLevels || [], currentUserId);
      const myStatus = myEntry ? (myEntry.status || "PENDING").toUpperCase() : null;
      const st = isAdmin ? (r.auditStatus || "PENDING").toUpperCase() : (myStatus || (r.auditStatus || "PENDING").toUpperCase());
      const f = statusFilter.toUpperCase();
      if (f === "APPROVED" && !["ACCEPTED", "APPROVED"].includes(st)) return false;
      if (f !== "APPROVED" && st !== f) return false;
    }
    const rowStart = r.fromDate ? new Date(r.fromDate) : null;
    const rowEnd   = r.toDate ? new Date(r.toDate) : rowStart;
    if (fromDate && rowEnd && rowEnd < new Date(fromDate)) return false;
    if (toDate && rowStart && rowStart > new Date(toDate)) return false;
    if (userFilter && String(r.submittedBy) !== userFilter) return false;
    if (locationFilter && String(r.locationId) !== locationFilter) return false;
    return true;
  });

  const pendingRows  = rows.filter(r => ["PENDING", "IN_REVIEW"].includes(r.auditStatus || "PENDING"));
  const approvedRows = rows.filter(r => ["ACCEPTED", "APPROVED"].includes(r.auditStatus));
  const rejectedRows = rows.filter(r => r.auditStatus === "REJECTED");
  const hasFilters   = !!(fromDate || toDate || statusFilter !== "PENDING" || userFilter || locationFilter);

  const actionable = displayed.filter(r => {
    const lvls = r.auditLevels || [];
    const myEntry = resolveMyEntry(lvls, currentUserId);
    if (!myEntry) return false;
    if (!["PENDING", "IN_REVIEW"].includes((myEntry.status || "PENDING").toUpperCase())) return false;
    // eslint-disable-next-line eqeqeq
    if (Number(r.currentAuditorId) != Number(currentUserId)) return false;
    return prevLevelsAllApproved(lvls, myEntry);
  });

  const allSel  = actionable.length > 0 && actionable.every(r => selected.has(r.id));
  const someSel = actionable.some(r => selected.has(r.id));
  const toggleRow = id => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => allSel ? setSelected(new Set()) : setSelected(new Set(actionable.map(r => r.id)));
  const clearFilters = () => { setFromDate(""); setToDate(""); setStatusFilter("PENDING"); setUserFilter(""); setLocationFilter(""); };

  const doApproveAll = async () => {
    setApprovingAll(true);
    try {
      await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}auditAction`, {}, { questionId: Number(questionId), stepIds: actionable.map(r => r.id), financialYearId: Number(fyId), action: "ACCEPTED", remark: "", questionType, questionTitle: kpiTitle }, "POST");
      setConfirmApproveAll(false); await load(); notify(`${actionable.length} answer${actionable.length > 1 ? "s" : ""} approved`);
    } catch { notify("Failed to approve all", "error"); }
    finally { setApprovingAll(false); }
  };

  const doBulkApprove = async () => {
    const ids = [...selected]; if (!ids.length) return;
    setBulkSaving(true);
    try {
      await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}auditAction`, {}, { questionId: Number(questionId), stepIds: ids, financialYearId: Number(fyId), action: "ACCEPTED", remark: "", questionType, questionTitle: kpiTitle }, "POST");
      setBulkApproveConfirm(false); await load(); notify(`${ids.length} answer${ids.length > 1 ? "s" : ""} approved`);
    } catch { notify("Failed", "error"); }
    finally { setBulkSaving(false); }
  };

  const bulkReject = async (remark) => {
    const ids = [...selected]; if (!ids.length) return;
    setBulkSaving(true);
    try {
      await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}auditAction`, {}, { questionId: Number(questionId), stepIds: ids, financialYearId: Number(fyId), action: "REJECTED", remark, questionType, questionTitle: kpiTitle }, "POST");
      setBulkRejectOpen(false); await load(); notify(`${ids.length} answer${ids.length > 1 ? "s" : ""} rejected`);
    } catch { notify("Failed", "error"); }
    finally { setBulkSaving(false); }
  };

  return (
    <div style={{ display: "flex", background: "#f0f2f5", minHeight: "100vh" }}>
      <div style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px", position: "sticky", top: 0, height: "100vh", background: "#fff", borderRight: "1px solid #e5e7eb", zIndex: 100, transition: "flex .2s" }}>
        <Sidebar dataFromParent={props.location?.pathname || "/dashboard"} onSidebarToggle={setSidebarExpanded} />
      </div>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        <Header />

        {/* Page header */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "15px 24px 0", flexShrink: 0 }}>
          {/* Breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, fontSize: 12.5 }}>
            <button onClick={() => router.goBack()} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 12.5, color: "#9ca3af", padding: 0 }}>← Back</button>
            <span style={{ color: "#e5e7eb" }}>/</span>
            <span style={{ color: "#374151", fontWeight: 600 }}>Answer Review</span>
            {auditType && (
              <span style={{ padding: "1px 8px", borderRadius: 4, fontSize: 10.5, fontWeight: 600, background: auditType === "INTERNAL" ? "#eff6ff" : "#f5f3ff", color: auditType === "INTERNAL" ? "#2563eb" : "#7c3aed", textTransform: "uppercase", letterSpacing: ".04em" }}>
                {auditType}
              </span>
            )}
          </div>

          {/* Title + stats */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 15, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ fontSize: 17, fontWeight: 700, color: "#111827", margin: "0 0 5px", letterSpacing: "-.02em", wordBreak: "break-word" }}>
                {loading ? <div style={{ height: 20, width: 260, borderRadius: 4, background: "#f3f4f6", display: "inline-block" }} /> : kpiTitle}
              </h1>
              {isAdmin && <span style={{ padding: "1px 7px", borderRadius: 4, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 10, fontWeight: 600, color: "#d97706" }}>Admin View</span>}
            </div>

            {!loading && !error && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 9, flexShrink: 0 }}>
                <div style={{ display: "flex", gap: 7 }}>
                  <Stat label="Total"    value={rows.length}         color="#374151" bg="#f9fafb" border="#e5e7eb" />
                  <Stat label="Pending"  value={pendingRows.length}  color="#d97706" bg="#fffbeb" border="#fde68a" />
                  <Stat label="Approved" value={approvedRows.length} color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
                  {rejectedRows.length > 0 && <Stat label="Rejected" value={rejectedRows.length} color="#dc2626" bg="#fef2f2" border="#fecaca" />}
                </div>
                {actionable.length > 0 && (
                  <button className="audit-btn-approve" onClick={() => setConfirmApproveAll(true)} disabled={approvingAll} style={{ fontSize: 12.5, padding: "6px 16px" }}>
                    ✓ Approve All Pending ({actionable.length})
                  </button>
                )}
              </div>
            )}
          </div>

          <FilterBar
            fromDate={fromDate} onFromDate={setFromDate} toDate={toDate} onToDate={setToDate}
            fyId={fyId} onFyId={setFyId} statusFilter={statusFilter} onStatusFilter={setStatusFilter}
            userFilter={userFilter} onUserFilter={setUserFilter} locationFilter={locationFilter} onLocationFilter={setLocationFilter}
            fys={fys} userOptions={userOptions} locationList={locationList}
            hasFilters={hasFilters} onClear={clearFilters}
            displayedCount={displayed.length} totalCount={rows.length} loading={loading}
          />
        </div>

        {/* Bulk selection bar */}
        {someSel && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 24px", background: "#eff6ff", borderBottom: "1px solid #dbeafe", flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#2563eb" }}>{selected.size} selected</span>
            <div style={{ width: 1, height: 16, background: "#dbeafe" }} />
            <button className="audit-btn-approve" onClick={() => setBulkApproveConfirm(true)} disabled={bulkSaving} style={{ fontSize: 12, padding: "5px 14px" }}>✓ Approve</button>
            <button className="audit-btn-reject" onClick={() => setBulkRejectOpen(true)} disabled={bulkSaving} style={{ fontSize: 12, padding: "5px 14px" }}>✕ Reject</button>
            <button className="audit-btn-ghost" onClick={() => setSelected(new Set())} style={{ fontSize: 12, padding: "5px 12px" }}>Deselect</button>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div style={{ padding: "10px 24px", display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: toast.type === "error" ? "#dc2626" : "#16a34a", background: toast.type === "error" ? "#fef2f2" : "#f0fdf4", borderBottom: `1px solid ${toast.type === "error" ? "#fecaca" : "#bbf7d0"}`, flexShrink: 0 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: toast.type === "error" ? "#dc2626" : "#16a34a", flexShrink: 0 }} />
            {toast.msg}
          </div>
        )}

        {/* Table */}
        <div style={{ flex: 1, overflow: "auto", padding: "16px 22px 44px" }}>
          <div style={{ background: "#fff", borderRadius: 9, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 46 }} /><col />
                <col style={{ width: 110 }} /><col style={{ width: 82 }} />
                <col style={{ width: 155 }} /><col style={{ width: 62 }} />
                <col style={{ width: 108 }} /><col style={{ width: 230 }} />
                <col style={{ width: 4 }} />
              </colgroup>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1.5px solid #e5e7eb" }}>
                  <th style={{ width: 46, padding: "10px 6px 10px 16px", textAlign: "center" }}>
                    {actionable.length > 0 && (
                      <input type="checkbox" className="audit-checkbox" checked={allSel}
                        ref={el => { if (el) el.indeterminate = someSel && !allSel; }}
                        onChange={toggleAll}
                      />
                    )}
                  </th>
                  {[["Period"], ["Value"], ["Location"], ["Submitted By"], ["Doc", true], ["Status", true], ["Action"]].map(([label, center]) => (
                    <th key={label} style={{ padding: "10px 12px", fontSize: 10.5, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".07em", textAlign: center ? "center" : "left", whiteSpace: "nowrap" }}>
                      {label}
                    </th>
                  ))}
                  <th style={{ width: 4 }} />
                </tr>
              </thead>
              <tbody>
                {loading && [0, 1, 2, 3].map(i => <ShimmerRow key={i} i={i} />)}

                {!loading && error && (
                  <tr><td colSpan={9} style={{ padding: "52px 24px", textAlign: "center" }}>
                    <div style={{ fontWeight: 700, color: "#dc2626", marginBottom: 7 }}>Failed to load</div>
                    <div style={{ fontSize: 12.5, color: "#9ca3af", marginBottom: 14 }}>{error}</div>
                    <button className="audit-btn-secondary" onClick={() => load()}>Retry</button>
                  </td></tr>
                )}

                {!loading && !error && displayed.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: "64px 24px", textAlign: "center" }}>
                    <div style={{ fontSize: 30, marginBottom: 10, opacity: .2 }}>{rows.length === 0 ? "📋" : "🔍"}</div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 6 }}>
                      {rows.length === 0 ? "No answers to review" : "No records match filters"}
                    </div>
                    <div style={{ fontSize: 12.5, color: "#9ca3af" }}>
                      {rows.length === 0 ? "No answers submitted yet." : "Try adjusting the filters."}
                    </div>
                    {hasFilters && <button className="audit-btn-ghost" style={{ marginTop: 14 }} onClick={clearFilters}>Clear filters</button>}
                  </td></tr>
                )}

                {!loading && !error && displayed.map((row, i) => (
                  <AuditRow
                    key={row.id || i} row={row} idx={i}
                    questionId={questionId} fyId={fyId}
                    kpiTitle={kpiTitle} questionType={questionType}
                    onViewDoc={info => setDocModal(info)}
                    onDone={() => { notify("Saved"); load(); }}
                    selected={selected.has(row.id)}
                    onToggle={toggleRow}
                    isAdmin={isAdmin}
                    locationMap={locationMap}
                  />
                ))}
              </tbody>
            </table>

            {loading && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 9, padding: 13, color: "#9ca3af", fontSize: 12.5, borderTop: "1px solid #f3f4f6" }}>
                <span className="audit-spinner" style={{ width: 14, height: 14 }} /> Loading…
              </div>
            )}

            {!loading && !error && displayed.length > 0 && (
              <div style={{ padding: "9px 18px", borderTop: "1px solid #f3f4f6", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <span style={{ fontSize: 11.5, color: "#9ca3af" }}>
                  {displayed.length} record{displayed.length !== 1 ? "s" : ""}{hasFilters ? ` (filtered from ${rows.length})` : ""}
                </span>
                <div style={{ display: "flex", gap: 14 }}>
                  {[["Approved", "#16a34a", approvedRows.length], ["Pending", "#d97706", pendingRows.length], ["Rejected", "#dc2626", rejectedRows.length]]
                    .filter(([, , v]) => v > 0)
                    .map(([l, c, v]) => (
                      <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: c }}>
                        <span style={{ width: 5, height: 5, borderRadius: "50%", background: c }} />{v} {l}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {docModal && <DocModal info={docModal} onClose={() => setDocModal(null)} />}
      {bulkRejectOpen && <BulkRejectModal count={selected.size} saving={bulkSaving} onConfirm={bulkReject} onCancel={() => setBulkRejectOpen(false)} />}
      {confirmApproveAll && (
        <ConfirmModal title="Approve All Pending Answers" message={`This will approve ${actionable.length} pending answer${actionable.length !== 1 ? "s" : ""} at your audit level. This action cannot be undone.`} confirmLabel={`✓ Approve ${actionable.length}`} confirmCls="audit-btn-approve" saving={approvingAll} onConfirm={doApproveAll} onCancel={() => setConfirmApproveAll(false)} />
      )}
      {bulkApproveConfirm && (
        <ConfirmModal title="Approve Selected Answers" message={`This will approve ${selected.size} selected answer${selected.size !== 1 ? "s" : ""} at your audit level.`} confirmLabel={`✓ Approve ${selected.size}`} confirmCls="audit-btn-approve" saving={bulkSaving} onConfirm={doBulkApprove} onCancel={() => setBulkApproveConfirm(false)} />
      )}
    </div>
  );
};

export default AuditAnswerPage;